import { Link } from "react-router-dom";
import { Activity, ShieldCheck, LineChart, ArrowRight, Stethoscope, HeartPulse, Sparkles, ClipboardCheck, TrendingDown } from "lucide-react";

const STATS = [
  { num: "90%", label: "of sonographers report work-related pain" },
  { num: "1 in 5", label: "have left the profession due to injury" },
];

const FEATURES = [
  { icon: Activity, title: "Daily Pain Tracking", desc: "0–10 score with body-map locations, scan hours and patient counts logged in under a minute." },
  { icon: LineChart, title: "Injury Trend Monitoring", desc: "Personal trajectories and anonymized clinic-wide hotspots, surfaced in real time." },
  { icon: HeartPulse, title: "Stretching Library", desc: "Region-specific mobility and recovery protocols designed for sonographers." },
  { icon: Stethoscope, title: "Ergonomic Education", desc: "Probe grip, bed height, screen placement and posture interventions." },
  { icon: ShieldCheck, title: "HIPAA-Ready Architecture", desc: "Anonymized clinic dashboards. Managers never see private clinician data." },
  { icon: Sparkles, title: "Research-Ready Datasets", desc: "Designed from day one for university partnerships and occupational health studies." },
];

const STEPS = [
  { num: "01", icon: ClipboardCheck, title: "Check in daily", desc: "Sonographers log pain by body region, scan hours and patient load in under 60 seconds." },
  { num: "02", icon: HeartPulse, title: "Act on signals", desc: "Targeted stretches and ergonomic prompts are surfaced the moment a hotspot appears." },
  { num: "03", icon: TrendingDown, title: "Reduce injuries", desc: "Managers see anonymized clinic trends and intervene before pain becomes a chronic injury." },
];

export default function Home() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container-x pt-20 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="pill" data-testid="hero-pill">Sonographer Wellness Platform</span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              Protecting the people <span style={{ color: "var(--gold)" }}>behind the probe.</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-600 max-w-2xl leading-relaxed">
              Sonote helps sonographers and clinics prevent work-related musculoskeletal injuries through daily pain tracking,
              ergonomic education, targeted stretching and clinic-wide injury analytics.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/register" className="btn-gold" data-testid="hero-trial-btn">
                Start free trial <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to="/contact" className="btn-outline" data-testid="hero-demo-btn">Book a demo</Link>
              <Link to="/features" className="btn-ghost" data-testid="hero-learn-btn">Learn more</Link>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-6 max-w-2xl">
              {STATS.map((s) => (
                <div key={s.label} data-testid={`hero-stat-${s.num}`}>
                  <div className="text-2xl font-extrabold" style={{ color: "var(--ink)" }}>{s.num}</div>
                  <div className="text-xs text-zinc-500 mt-1 leading-snug">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border border-zinc-200 shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1691933880096-4046bc9e2fa0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxzb25vZ3JhcGhlciUyMHVsdHJhc291bmR8ZW58MHx8fHwxNzgyMTUxOTgzfDA&ixlib=rb-4.1.0&q=85"
                alt="Sonographer at ultrasound machine"
                className="w-full h-[460px] object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-md rounded-xl p-4 border border-zinc-200">
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Live</span>
                </div>
                <p className="text-sm mt-1 text-zinc-700">Clinic-wide pain index improving 12% this month.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission band */}
      <section className="bg-zinc-50 border-y border-zinc-200">
        <div className="container-x py-16 grid md:grid-cols-3 gap-10">
          <div>
            <h2 className="text-3xl font-bold">A silent occupational crisis.</h2>
          </div>
          <div className="md:col-span-2 text-zinc-600 leading-relaxed text-lg">
            Up to 90% of sonographers report work-related musculoskeletal pain. One in five leaves the profession due to injury.
            Sonote is built to keep your clinicians scanning—pain free—for entire careers.
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="container-x py-24">
        <div className="max-w-2xl">
          <span className="pill">Platform</span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold">Everything you need to protect your team.</h2>
          <p className="mt-3 text-zinc-600">From daily check-ins to clinic-wide analytics, Sonote turns prevention into a daily habit.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="card-marketing" data-testid={`feature-${title.toLowerCase().replace(/\s/g,'-')}`}>
              <span className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: "var(--gold-light)" }}>
                <Icon className="w-6 h-6" style={{ color: "var(--gold-hover)" }} />
              </span>
              <h3 className="mt-5 font-bold text-xl">{title}</h3>
              <p className="mt-2 text-zinc-600 text-sm leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-zinc-50 border-y border-zinc-200">
        <div className="container-x py-24">
          <div className="max-w-2xl">
            <span className="pill">How it works</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold">Prevention in three steps.</h2>
            <p className="mt-3 text-zinc-600">A daily rhythm that fits between scans—built around how sonographers actually work.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {STEPS.map(({ num, icon: Icon, title, desc }) => (
              <div
                key={num}
                className="relative bg-white border border-zinc-200 rounded-2xl p-7 hover:border-zinc-900 transition-colors"
                data-testid={`how-step-${num}`}
              >
                <div className="flex items-start justify-between">
                  <span
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ background: "var(--gold-light)" }}
                  >
                    <Icon className="w-6 h-6" style={{ color: "var(--gold-hover)" }} />
                  </span>
                  <span className="text-4xl font-black text-zinc-100 leading-none">{num}</span>
                </div>
                <h3 className="mt-5 font-bold text-xl">{title}</h3>
                <p className="mt-2 text-zinc-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-x py-24">
        <div className="rounded-3xl border border-zinc-200 p-12 text-center" style={{ background: "var(--ink)" }}>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Ready to protect your team?</h2>
          <p className="mt-4 text-zinc-300 max-w-2xl mx-auto">Start a free 14-day trial. No credit card required.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/register" className="btn-gold" data-testid="cta-trial-btn">Start free trial</Link>
            <Link to="/contact" className="btn-outline border-zinc-700 text-white bg-transparent hover:bg-white hover:text-zinc-900" data-testid="cta-demo-btn">Book a demo</Link>
          </div>
        </div>
      </section>
    </>
  );
}
